/**
 * Natural diamond quote button (#314).
 *
 * A tap on [data-natural-diamond-quote] opens the Podium web chat with a
 * ready-written message (data-message), sent to one Podium location
 * (data-location-uid, blank = the customer picks a store).
 *
 * - Podium loads after the page. A tap before it is ready is queued, not lost:
 *   we wait for Podium's own onLoad callback (or the mounted bubble), then open.
 * - The theme hides #podium-website-widget while the cart drawer or a product
 *   modal is open. While a chat we opened is on screen, body.ndq-chat-open
 *   overrides those rules (natural-diamond-quote.css).
 * - If the chat never appears (blocked, failed to load), the customer still
 *   gets through: a text message to the store on phones (data-sms-number),
 *   otherwise the button's own link (the contact page).
 * - Tracking uses the same method as the theme's appointment button
 *   (layout/theme.liquid): gtag('event') + fbq('trackCustom').
 *     natural_diamond_quote_click   on every tap
 *     natural_diamond_quote_submit  when Podium reports "Conversation Started"
 *                                   for a chat this button opened
 *   dataLayer.push is never wrapped or replaced (that breaks GTM); Podium's own
 *   dataLayer entries are READ by polling.
 */
(function () {
  'use strict';

  if (window.LindelliNaturalDiamondQuote) return;

  var LOG_PREFIX = '[natural-diamond-quote]';
  var READY_TIMEOUT_MS = 8000;
  var READY_POLL_MS = 150;
  var OPEN_VERIFY_MS = 900;
  var MAX_OPEN_ATTEMPTS = 3;
  var WATCH_POLL_MS = 500;
  var WATCH_MAX_MS = 30 * 60 * 1000;
  var BODY_OPEN_CLASS = 'ndq-chat-open';
  var PODIUM_EVENT_CATEGORY = 'podium';
  var PODIUM_SUBMIT_ACTION = 'Conversation Started';
  var PODIUM_CLOSE_ACTION = 'Widget Closed';

  var podiumLoaded = false;
  var busy = false;
  var watcher = null;

  function warn(message, detail) {
    if (window.console && typeof window.console.warn === 'function') {
      window.console.warn(LOG_PREFIX + ' ' + message, detail === undefined ? '' : detail);
    }
  }

  function track(eventName, params) {
    try {
      if (typeof window.gtag === 'function') window.gtag('event', eventName, params);
    } catch (error) {
      warn('gtag failed for ' + eventName, error);
    }
    try {
      if (typeof window.fbq === 'function') window.fbq('trackCustom', eventName, params);
    } catch (error) {
      warn('fbq failed for ' + eventName, error);
    }
  }

  // Chain onto Podium's documented load callback without dropping one that
  // something else on the page may already have set.
  function listenForPodiumLoad() {
    var chat = (window.PodiumWebChat = window.PodiumWebChat || {});
    var previous = chat.onLoad;
    if (previous && previous.__ndq) return;
    var onLoad = function () {
      podiumLoaded = true;
      if (typeof previous === 'function') {
        try {
          return previous.apply(this, arguments);
        } catch (error) {
          warn('existing PodiumWebChat.onLoad threw', error);
        }
      }
    };
    onLoad.__ndq = true;
    chat.onLoad = onLoad;
  }

  function podiumReady() {
    var chat = window.PodiumWebChat;
    if (!chat || typeof chat.open !== 'function') return false;
    // onLoad may have fired before this script ran; the mounted bubble is the
    // same signal read from the page.
    return podiumLoaded || !!document.getElementById('podium-bubble');
  }

  function chatVisible() {
    var modal = document.getElementById('podium-modal');
    if (!modal) return false;
    var rect = modal.getBoundingClientRect();
    return rect.width > 0 && rect.height > 0;
  }

  function waitFor(condition, timeoutMs, done) {
    var started = Date.now();
    (function check() {
      if (condition()) return done(true);
      if (Date.now() - started >= timeoutMs) return done(false);
      window.setTimeout(check, READY_POLL_MS);
    })();
  }

  function isTouchPhone() {
    return !!(window.matchMedia && window.matchMedia('(pointer: coarse)').matches) &&
      window.innerWidth < 1024;
  }

  function fallback(button, message, reason) {
    warn('Podium chat unavailable, using fallback (' + reason + ')');
    document.body.classList.remove(BODY_OPEN_CLASS);
    var smsNumber = button.getAttribute('data-sms-number');
    if (smsNumber && isTouchPhone()) {
      // "?&body=" is read by both iOS and Android messaging apps.
      window.location.href = 'sms:' + smsNumber + '?&body=' + encodeURIComponent(message);
      return;
    }
    var href = button.getAttribute('href');
    if (href) window.location.href = href;
  }

  function openChat(button, message, attempt) {
    var options = { message: message };
    var locationUid = button.getAttribute('data-location-uid');
    if (locationUid) options.locationUid = locationUid;

    try {
      window.PodiumWebChat.open(options);
    } catch (error) {
      warn('PodiumWebChat.open threw', error);
    }

    window.setTimeout(function () {
      if (chatVisible()) {
        busy = false;
        return;
      }
      if (attempt < MAX_OPEN_ATTEMPTS) {
        openChat(button, message, attempt + 1);
        return;
      }
      busy = false;
      stopWatching();
      fallback(button, message, 'chat did not appear after ' + MAX_OPEN_ATTEMPTS + ' attempts');
    }, OPEN_VERIFY_MS);
  }

  function podiumDataLayer() {
    var mapped = window.PodiumGTMDataLayerMapping && window[window.PodiumGTMDataLayerMapping];
    return mapped || window.dataLayer || null;
  }

  function stopWatching() {
    if (!watcher) return;
    window.clearInterval(watcher.timer);
    if (watcher.cartObserver) watcher.cartObserver.disconnect();
    watcher = null;
  }

  // Watch (never wrap) the dataLayer for Podium's own events after our tap.
  function startWatching(params) {
    stopWatching();
    var layer = podiumDataLayer();
    var current = {
      started: Date.now(),
      index: layer ? layer.length : 0,
      params: params,
      timer: null,
      cartObserver: null,
    };

    current.timer = window.setInterval(function () {
      if (Date.now() - current.started > WATCH_MAX_MS) {
        stopWatching();
        return;
      }
      var entries = podiumDataLayer();
      if (!entries) return;
      for (; current.index < entries.length; current.index++) {
        var entry = entries[current.index];
        if (!entry || entry.eventCategory !== PODIUM_EVENT_CATEGORY) continue;
        if (entry.eventAction === PODIUM_SUBMIT_ACTION) {
          track('natural_diamond_quote_submit', current.params);
          stopWatching();
          return;
        }
        if (entry.eventAction === PODIUM_CLOSE_ACTION) {
          // Closed without sending: a later chat from the normal bubble is
          // not this button's enquiry.
          document.body.classList.remove(BODY_OPEN_CLASS);
          stopWatching();
          return;
        }
      }
    }, WATCH_POLL_MS);

    // Opening the cart after the chat should hide the chat again, as before.
    if (window.MutationObserver) {
      current.cartObserver = new window.MutationObserver(function () {
        if (document.body.getAttribute('data-cart-active') === 'active') {
          document.body.classList.remove(BODY_OPEN_CLASS);
        }
      });
      current.cartObserver.observe(document.body, { attributes: true, attributeFilter: ['data-cart-active'] });
    }

    watcher = current;
  }

  function onClick(event) {
    var button = event.target.closest && event.target.closest('[data-natural-diamond-quote]');
    if (!button) return;
    // Let modified clicks (new tab etc.) follow the fallback link untouched.
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || event.button > 0) return;
    event.preventDefault();

    var message = button.getAttribute('data-message') || '';
    var params = {
      button: 'Natural diamond quote',
      product_id: button.getAttribute('data-product-id') || '',
      product_name: button.getAttribute('data-product-title') || '',
    };
    // A second tap while the first is still opening the chat is not a new click.
    if (busy) return;
    busy = true;
    track('natural_diamond_quote_click', params);
    document.body.classList.add(BODY_OPEN_CLASS);
    startWatching(params);

    waitFor(podiumReady, READY_TIMEOUT_MS, function (ready) {
      if (!ready) {
        busy = false;
        stopWatching();
        fallback(button, message, 'Podium not ready after ' + READY_TIMEOUT_MS + 'ms');
        return;
      }
      openChat(button, message, 1);
    });
  }

  listenForPodiumLoad();
  document.addEventListener('click', onClick);

  window.LindelliNaturalDiamondQuote = {
    // Exposed for QA in the browser console; not used by the theme.
    isPodiumReady: podiumReady,
    isChatVisible: chatVisible,
  };
})();
